import { useClientPortalAPI, useQueryParams, useSubmit } from "@certn/api";
import {
  Loader,
  Pagination,
  Picker,
  SectionMessage,
} from "@certn/cascadia-react";
import {
  ActionList,
  BasicButton,
  Stack,
  Text,
  showToast,
} from "@certn/cascadia";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { ClearFilters } from "../../components/filters/ClearFilters";
import { DocumentItem } from "./components/DocumentItem";
import isEmpty from "lodash/isEmpty";
import { downloadFile } from "../legacy-case-details/utils/legacyCaseDownloads";
import { Schemas } from "../../types";
import { useState } from "react";

interface DocumentsTabContainerProps {
  caseId: string;
}

export function DocumentsTabContainer({ caseId }: DocumentsTabContainerProps) {
  const { t } = useTranslation();
  const [jobId, setJobId] = useState<string | null>(null);

  const {
    params: queryParams,
    getTypedParamsAsArray,
    setParam,
  } = useQueryParams();

  const allowed_types: Schemas["CaseDocumentType"][] = [
    "SOURCE_EVIDENCE",
    "CONSENT_DOCUMENT",
    "GAP_DOCUMENT",
    "CV_RESUME",
    "EDUCATION_DOCUMENT",
  ];
  const document_type =
    getTypedParamsAsArray<Schemas["CaseDocumentType"]>("document_type");
  const page = queryParams.page ? Number(queryParams.page) : 1;
  const page_size = queryParams["page_size"]
    ? Number(queryParams["page_size"])
    : 10;

  const { data, error, isLoading } = useClientPortalAPI(
    `/api/internal/client-portal/case-documents/`,
    {
      case: caseId,
      page,
      page_size,
      allowed_types,
      ...(document_type.length ? { document_type: document_type } : {}),
    }
  );

  const { submit: startDownloadAll, isSubmitting: isDownloading } = useSubmit<
    Schemas["CaseDocumentDownloadAllAccepted"],
    Schemas["CaseDocumentDownloadAllRequest"]
  >("/api/internal/client-portal/case-documents/download-all/", {
    showToastOnError: t(
      "Unable to download documents. Please try again later."
    ),
    onSuccess: (data?: Schemas["CaseDocumentDownloadAllAccepted"]) => {
      if (data) {
        setJobId(data.job_id);
      }
    },
  });

  const { data: jobStatus } = useClientPortalAPI(
    jobId
      ? `/api/internal/client-portal/case-documents/download-all/${jobId}/status/`
      : null,
    undefined,
    {
      refreshInterval: () => {
        if (!jobId) return 0;

        if (
          jobStatus?.status === "complete" ||
          jobStatus?.status === "failed"
        ) {
          return 0;
        }

        return 2000;
      },
      onSuccess: async (
        data: Schemas["CaseDocumentDownloadStatusResponse"]
      ) => {
        if (data.status === "complete" && data.download_url) {
          setJobId(null);
          await downloadFile(data.download_url, `documents-${caseId}.zip`);
        } else if (data?.status === "failed") {
          showToast(
            data.error ??
              t(`Something went wrong: {{error}}`, {
                error: JSON.stringify(data.error),
              }),
            { type: "error" }
          );

          setJobId(null);
        }
      },
    }
  );

  const downloadAllFiles = async () => {
    await startDownloadAll({
      case: caseId,
      allowed_types,
      ...(document_type.length ? { document_type } : {}),
    });
  };

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <SectionMessage
        type="danger"
        icon="error"
        sectionTitle={t("Unable to retrieve documents")}
      >
        {String(error.message)}
      </SectionMessage>
    );
  }

  const documentTypeFilter = data?.filters?.find(
    (f) => f.key === "document_type"
  );

  return (
    <Stack
      gap="space-4"
      className={css({
        padding: "var(--space-6)",
        height: "100%",
      })}
    >
      <Text style="global/title-small">{t("Documents")}</Text>
      <Stack orientation="horizontal" justify="space-between">
        <Stack orientation="horizontal" gap="space-2">
          {documentTypeFilter && (
            <Picker
              showActionBar
              selectedKeys={getTypedParamsAsArray("document_type")}
              onSelectionChange={(keys) =>
                setParam("document_type", Array.from(keys).map(String), true)
              }
              label={t("Document type")}
              items={documentTypeFilter.options.map((option) => ({
                id: option.key,
                name: option.label,
              }))}
              selectionMode="multiple"
              testID="document-type-filter"
            />
          )}
          <ClearFilters />
        </Stack>
        <BasicButton
          onPress={downloadAllFiles}
          isDisabled={isDownloading || !!jobId || !data?.results?.length}
          size="small"
          rightIcon="download"
        >
          {isDownloading || !!jobId ? t("Downloading...") : t("Download All")}
        </BasicButton>
      </Stack>

      <Stack gap="space-6">
        {!data || isEmpty(data.results) ? (
          <SectionMessage
            type="info"
            icon="info"
            sectionTitle={t("No documents found.")}
          />
        ) : (
          <ActionList testID="documents-action-list" hideBorder showDividers>
            {data.results.map((document) => (
              <DocumentItem
                key={document.id}
                document={document}
                onViewDocument={(file_url) => {
                  window.open(file_url, "_blank");
                }}
              />
            ))}
          </ActionList>
        )}
        <Pagination
          className={css({
            paddingLeft: "var(--space-4)",
            paddingRight: "var(--space-4)",
          })}
          total={data?.count ?? 0}
          page={page}
          pageCount={page_size}
        />
      </Stack>
    </Stack>
  );
}
